import { memo } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import {
  formatFixedNumberCurrency,
  formatFloatingPointNumber,
} from "utils/number-format"

import { CellError } from "./CellError"
import { ChangedPlanRecord } from "../types"
import { Info } from "./Info"
import Input from "ui/inputs/Input"
import { Tooltip } from "@material-ui/core"
import { css } from "@emotion/css"
import { get } from "lodash"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { usePlanCachedData } from "../utils"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"
import { roundHalfEven } from "../components/AutoCompute/utils"

export const PreciseReadonlyCell = memo(function _PreciseReadonlyCell(
  props: CellProps,
) {
  const precision = usePrecision()
  return <ReadonlyCell {...props} precision={precision} />
})

export const ReadonlyCell = memo(function _ReadonlyCell(props: CellProps) {
  const {
    rowId: id,
    dataKey,
    columnId,
    format,
    primaryKey,
    semantic,
    editType,
    precision = DEFAULT_PRECISION,
  } = props
  const monetary = format === "MONETARY"
  const tableSource = useTableSource()
  const aggregations = useAtomValue(tableAggregations)
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)

  const isBooking = primaryKey === "buyingMediaFields"
  const planData = usePlanCachedData(isBooking)

  const values = readFrom[id] || {}
  const isMediaSubmedia =
    values.type === "media_type_plan" || values.type === "media_subtype_plan"

  const error = get(readError, `${id}.${columnId}`)
  const value = String(get(readFrom, `${id}.${dataKey}`) ?? "")
  const packaged = values?.packaged
  const aggregatedValue = get(aggregations, [id, columnId])
  const hasAggregation = !!Object.keys(aggregations).includes(id)
  let appliedValue =
    isMediaSubmedia && hasAggregation && !isBooking && !packaged
      ? String(aggregatedValue ?? value)
      : value

  appliedValue = appliedValue
    ? roundHalfEven(Number(appliedValue), precision).toString()
    : appliedValue

  const fvalue = monetary
    ? !!appliedValue
      ? formatFloatingPointNumber(precision)(parseFloat(appliedValue))
      : ""
    : appliedValue

  const { getInputProps } = useFocusManagement()

  // apply billing type rules
  const viewable = useBillingTypesRules(
    values as ChangedPlanRecord,
    primaryKey,
    semantic,
  )

  if (!viewable || (values.packaged && editType !== "Calculated"))
    return <span />

  return viewable ? (
    <div className={styles.wrapper}>
      <Tooltip title={fvalue} placement="bottom-end">
        <Input
          {...getInputProps()}
          id={`${id}/${dataKey}`}
          className={styles.input}
          value={
            monetary && fvalue
              ? formatFixedNumberCurrency(
                  monetary ? `${fvalue}${"0".repeat(precision)}` : fvalue,
                  planData?.currencyCode,
                  precision,
                )
              : fvalue
          }
          disabled={true}
        />
      </Tooltip>
      <Info {...props} />
      {error ? <CellError error={error} /> : null}
    </div>
  ) : (
    <span />
  )
})

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
  `,
  input: css`
    font-size: 13px;
    font-weight: inherit;
    text-align: end;
  `,
}
